import {
  isZip,
  readZipEntries,
  readZipEntryData,
  ZipEntrySizeLimitError,
  type ReadZipEntryDataOptions,
  type ZipEntry,
} from "./zip.js";
import { measurePerformance, measurePerformanceAsync } from "../perf/index.js";

export type RqtPackArchiveType = "zip" | "tar" | "unknown";
export type RqtPackPermissionValue = boolean | "ask";
export type RqtPackPermissionDecision = "allow" | "ask" | "deny";

export interface RqtPackManifest {
  id: string;
  name: string;
  version: string;
  packVersion: string;
  languageVersion: string;
  type: string;
  features?: string[];
  description?: string;
  publisher?: Record<string, unknown>;
  entry: {
    game?: string;
    script?: string;
    compiled?: string;
    startup?: string;
  };
  compatibility?: {
    minLauncherVersion?: string;
    testedLauncherVersion?: string;
    [key: string]: unknown;
  };
  permissions?: Record<string, RqtPackPermissionValue>;
  recommendations?: Record<string, unknown>;
  recommendedRuntime?: Record<string, unknown>;
  modelCompatibility?: Record<string, unknown>;
  dependencies?: Record<string, string>;
  contentRating?: {
    age?: string | number;
    flags?: string[];
    requiresAgeGate?: boolean;
    [key: string]: unknown;
  };
  assets?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface RqtPackIssue {
  code: string;
  message: string;
  path?: string;
}

export interface RqtPackEntry {
  path: string;
  size: number;
  kind: "manifest" | "script" | "compiled" | "asset" | "character" | "document" | "other";
}

export interface RqtPackPermissionPreview {
  name: string;
  decision: RqtPackPermissionDecision;
  sensitive: boolean;
  group: string;
}

export interface RqtPackRecommendationPreview {
  key: string;
  value: unknown;
  source: "recommendations" | "recommendedRuntime" | "modelCompatibility";
}

export interface RqtPackAnalysis {
  archiveType: RqtPackArchiveType;
  manifest?: RqtPackManifest;
  entries: RqtPackEntry[];
  scripts: string[];
  assets: string[];
  characters: string[];
  permissions: RqtPackPermissionPreview[];
  recommendations: RqtPackRecommendationPreview[];
  errors: RqtPackIssue[];
  warnings: RqtPackIssue[];
  installable: boolean;
}

interface ArchiveEntry {
  path: string;
  size: number;
  read(options?: ReadZipEntryDataOptions): Promise<Uint8Array | undefined>;
}

interface ArchiveReadResult {
  type: RqtPackArchiveType;
  entries: ArchiveEntry[];
}

const MANIFEST_PATH = "manifest.json";
const REQUIRED_MANIFEST_FIELDS = ["id", "name", "version", "packVersion", "languageVersion", "type"] as const;
const TEXT_DECODER = new TextDecoder("utf-8");
const KIB = 1024;
const RQTPACK_MANIFEST_LIMIT_BYTES = 256 * KIB;

export async function analyzeRqtPack(bytes: Uint8Array): Promise<RqtPackAnalysis> {
  const errors: RqtPackIssue[] = [];
  const warnings: RqtPackIssue[] = [];
  const archive = await readArchive(bytes);
  const safeEntries: ArchiveEntry[] = [];

  if (archive.type === "unknown") {
    errors.push({
      code: "RPK001",
      message: ".rqp must be a ZIP or TAR archive.",
    });
  }

  for (const entry of archive.entries) {
    const pathError = validatePackagePath(entry.path);
    if (pathError) {
      errors.push({ code: "RPK002", message: pathError, path: entry.path });
      continue;
    }
    safeEntries.push(entry);
  }

  const manifestEntry = safeEntries.find((entry) => entry.path === MANIFEST_PATH);
  if (!manifestEntry) {
    errors.push({
      code: "RPK003",
      message: ".rqp must contain manifest.json at the archive root.",
    });
  }

  const manifest = manifestEntry
    ? await measurePerformanceAsync(
        "load_rqtpack_manifest",
        {
          inputSize: manifestEntry.size,
          fileId: manifestEntry.path,
          packageId: "",
          cache: "bypass",
          thread: "main",
          invalidationReason: "package_bytes_loaded",
        },
        () => readManifest(manifestEntry, errors),
      )
    : undefined;
  if (manifest && manifestEntry) validateManifest(manifest, safeEntries, errors, warnings, manifestEntry.path);

  const entries = measurePerformance(
    "load_rqtpack_assets_index",
    {
      inputSize: safeEntries.reduce((n, entry) => n + entry.size, 0),
      fileId: "archive-index",
      packageId: manifest?.id ?? "",
      cache: "bypass",
      thread: "main",
      invalidationReason: "package_bytes_loaded",
      detail: { entries: safeEntries.length },
    },
    () =>
      safeEntries
        .map((entry) => ({ path: entry.path, size: entry.size, kind: classifyEntry(entry.path) }))
        .sort((a, b) => a.path.localeCompare(b.path)),
  );
  const scripts = entries.filter((entry) => entry.kind === "script").map((entry) => entry.path);
  const assets = entries.filter((entry) => entry.kind === "asset").map((entry) => entry.path);
  const characters = entries.filter((entry) => entry.kind === "character").map((entry) => entry.path);
  const permissions = manifest ? normalizePermissions(manifest, warnings) : [];
  const recommendations = manifest ? normalizeRecommendations(manifest) : [];

  return {
    archiveType: archive.type,
    ...(manifest ? { manifest } : {}),
    entries,
    scripts,
    assets,
    characters,
    permissions,
    recommendations,
    errors,
    warnings,
    installable: errors.length === 0 && !!manifest,
  };
}

async function readArchive(bytes: Uint8Array): Promise<ArchiveReadResult> {
  if (isZip(bytes)) {
    return { type: "zip", entries: readZipArchiveEntries(bytes) };
  }
  const tarEntries = readTarArchiveEntries(bytes);
  if (tarEntries.length > 0) {
    return { type: "tar", entries: tarEntries };
  }
  return { type: "unknown", entries: [] };
}

function readZipArchiveEntries(bytes: Uint8Array): ArchiveEntry[] {
  return readZipEntries(bytes)
    .filter((entry) => !entry.name.endsWith("/"))
    .map((entry) => ({
      path: entry.name,
      size: entry.uncompressedSize,
      read: (options) => readZipEntryData(bytes, entry, options),
    }));
}

interface TarEntry {
  name: string;
  size: number;
  dataStart: number;
  dataEnd: number;
}

function readTarArchiveEntries(bytes: Uint8Array): ArchiveEntry[] {
  const entries: TarEntry[] = [];
  for (let offset = 0; offset + 512 <= bytes.length;) {
    const header = bytes.subarray(offset, offset + 512);
    if (isZeroBlock(header)) break;

    const name = readTarString(header, 0, 100);
    const prefix = readTarString(header, 345, 155);
    const path = prefix ? `${prefix}/${name}` : name;
    const size = readTarOctal(header, 124, 12);
    if (!path || size < 0) return [];

    const typeflag = String.fromCharCode(header[156] ?? 0);
    const dataStart = offset + 512;
    const dataEnd = dataStart + size;
    if (dataEnd > bytes.length) return [];

    if (typeflag === "\0" || typeflag === "0" || typeflag === "") {
      entries.push({ name: path, size, dataStart, dataEnd });
    }

    offset = dataStart + Math.ceil(size / 512) * 512;
  }

  return entries.map((entry) => ({
    path: entry.name,
    size: entry.size,
    read: async (options) => {
      const data = bytes.subarray(entry.dataStart, entry.dataEnd);
      if (options?.maxBytes !== undefined && data.byteLength > options.maxBytes) return undefined;
      return data.slice();
    },
  }));
}

function isZeroBlock(block: Uint8Array): boolean {
  for (const byte of block) {
    if (byte !== 0) return false;
  }
  return true;
}

function readTarString(block: Uint8Array, start: number, length: number): string {
  const slice = block.subarray(start, start + length);
  const nul = slice.indexOf(0);
  return TEXT_DECODER.decode(nul >= 0 ? slice.subarray(0, nul) : slice).trim();
}

function readTarOctal(block: Uint8Array, start: number, length: number): number {
  const text = readTarString(block, start, length).replace(/\0/g, "").trim();
  if (!text) return 0;
  if (!/^[0-7]+$/.test(text)) return -1;
  return Number.parseInt(text, 8);
}

async function readManifest(entry: ArchiveEntry, errors: RqtPackIssue[]): Promise<RqtPackManifest | undefined> {
  if (entry.size > RQTPACK_MANIFEST_LIMIT_BYTES) {
    errors.push({
      code: "RPK023",
      message: `${entry.path} is too large. Maximum size is ${formatKib(RQTPACK_MANIFEST_LIMIT_BYTES)}.`,
      path: entry.path,
    });
    return undefined;
  }

  let data: Uint8Array | undefined;
  try {
    data = await entry.read({ maxBytes: RQTPACK_MANIFEST_LIMIT_BYTES });
  } catch (error) {
    if (error instanceof ZipEntrySizeLimitError) {
      errors.push({
        code: "RPK023",
        message: `${entry.path} is too large. Maximum size is ${formatKib(RQTPACK_MANIFEST_LIMIT_BYTES)}.`,
        path: entry.path,
      });
      return undefined;
    }
    throw error;
  }
  if (!data) {
    errors.push({
      code: "RPK004",
      message: `${entry.path} could not be read from the archive.`,
      path: entry.path,
    });
    return undefined;
  }
  if (data.byteLength > RQTPACK_MANIFEST_LIMIT_BYTES) {
    errors.push({
      code: "RPK023",
      message: `${entry.path} is too large. Maximum size is ${formatKib(RQTPACK_MANIFEST_LIMIT_BYTES)}.`,
      path: entry.path,
    });
    return undefined;
  }

  try {
    const parsed = JSON.parse(TEXT_DECODER.decode(data)) as unknown;
    if (!isRecord(parsed)) {
      errors.push({
        code: "RPK005",
        message: `${entry.path} must contain a JSON object.`,
        path: entry.path,
      });
      return undefined;
    }
    return parsed as RqtPackManifest;
  } catch {
    errors.push({
      code: "RPK006",
      message: `${entry.path} is not valid JSON.`,
      path: entry.path,
    });
    return undefined;
  }
}

function formatKib(bytes: number): string {
  return `${Math.round(bytes / KIB)} KB`;
}

function validateManifest(
  manifest: RqtPackManifest,
  entries: ArchiveEntry[],
  errors: RqtPackIssue[],
  warnings: RqtPackIssue[],
  manifestPath: string,
): void {
  for (const field of REQUIRED_MANIFEST_FIELDS) {
    if (!isNonEmptyString(manifest[field])) {
      errors.push({
        code: "RPK007",
        message: `${manifestPath} requires a non-empty "${field}" field.`,
        path: manifestPath,
      });
    }
  }

  if (!isRecord(manifest.entry)) {
    errors.push({
      code: "RPK008",
      message: `${manifestPath} requires entry.game or entry.script.`,
      path: manifestPath,
    });
    return;
  }
  const entryGame = isNonEmptyString(manifest.entry["game"]) ? String(manifest.entry.game) : "";
  const entryScript = isNonEmptyString(manifest.entry["script"]) ? String(manifest.entry.script) : "";
  if (!entryGame && !entryScript) {
    errors.push({
      code: "RPK008",
      message: `${manifestPath} requires entry.game or entry.script.`,
      path: manifestPath,
    });
    return;
  }

  if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(String(manifest.id))) {
    errors.push({
      code: "RPK009",
      message: "manifest id must be a namespaced ASCII identifier.",
      path: manifestPath,
    });
  } else if (!String(manifest.id).includes(".")) {
    warnings.push({
      code: "RPK010",
      message: "manifest id should include a namespace, for example creator.pack_name.",
      path: manifestPath,
    });
  }

  if (entryGame) {
    const gamePathError = validatePackagePath(entryGame);
    if (gamePathError) {
      errors.push({ code: "RPK019", message: `entry.game is unsafe: ${gamePathError}`, path: entryGame });
    } else if (!entries.some((entry) => entry.path === entryGame)) {
      errors.push({
        code: "RPK020",
        message: "entry.game does not point to a file in the package.",
        path: entryGame,
      });
    } else if (!entryGame.endsWith(".rqg")) {
      errors.push({
        code: "RPK021",
        message: "entry.game must point to a .rqg runtime document.",
        path: entryGame,
      });
    }
  }

  if (entryScript) {
    const pathError = validatePackagePath(entryScript);
    if (pathError) {
      errors.push({ code: "RPK011", message: `entry.script is unsafe: ${pathError}`, path: entryScript });
    } else if (!entries.some((entry) => entry.path === entryScript)) {
      errors.push({
        code: "RPK012",
        message: "entry.script does not point to a file in the package.",
        path: entryScript,
      });
    } else if (!entryScript.startsWith("source/") && !entryScript.startsWith("scripts/")) {
      warnings.push({
        code: "RPK013",
        message: "entry.script should live under source/ for .rqp packages. scripts/ is accepted for legacy packages.",
        path: entryScript,
      });
    }
  }

  if (manifest.entry.compiled) {
    const compiledPath = String(manifest.entry.compiled);
    const compiledPathError = validatePackagePath(compiledPath);
    if (compiledPathError) {
      errors.push({ code: "RPK014", message: `entry.compiled is unsafe: ${compiledPathError}`, path: compiledPath });
    } else if (!entries.some((entry) => entry.path === compiledPath)) {
      warnings.push({
        code: "RPK015",
        message: "entry.compiled does not point to a file in the package.",
        path: compiledPath,
      });
    } else if (!compiledPath.endsWith(".rqg")) {
      warnings.push({
        code: "RPK022",
        message: "entry.compiled should point to a .rqg runtime document.",
        path: compiledPath,
      });
    }
  }

  if (manifest.contentRating?.requiresAgeGate) {
    warnings.push({
      code: "RPK016",
      message: "This package declares content that requires an age gate before installation.",
      path: manifestPath,
    });
  }
}

function validatePackagePath(path: string): string | undefined {
  if (!path) return "path is empty.";
  if (path.includes("\0")) return "path contains a NUL byte.";
  if (path.includes("\\")) return "path must use forward slashes only.";
  if (path.startsWith("/")) return "absolute paths are not allowed.";
  if (/^[A-Za-z]:/.test(path)) return "drive-letter paths are not allowed.";

  const parts = path.split("/");
  if (parts.some((part) => part === "")) return "empty path segments are not allowed.";
  if (parts.some((part) => part === "." || part === "..")) return "relative path segments are not allowed.";
  return undefined;
}

function classifyEntry(path: string): RqtPackEntry["kind"] {
  if (path === MANIFEST_PATH) return "manifest";
  if ((path.startsWith("source/") || path.startsWith("scripts/")) && path.endsWith(".rqt")) return "script";
  if (path.endsWith(".rqg")) return "compiled";
  if (path.startsWith("assets/")) return "asset";
  if (path.startsWith("characters/")) return "character";
  if (path.startsWith("docs/") || path.toLowerCase().endsWith(".md")) return "document";
  return "other";
}

function normalizePermissions(manifest: RqtPackManifest, warnings: RqtPackIssue[]): RqtPackPermissionPreview[] {
  const raw = manifest.permissions;
  if (!raw) return [];

  if (!isRecord(raw)) {
    warnings.push({
      code: "RPK017",
      message: "manifest.permissions should be an object keyed by dotted permission names.",
      path: MANIFEST_PATH,
    });
    return [];
  }

  return Object.entries(raw)
    .filter(([name, value]) => {
      const ok = isNonEmptyString(name) && (value === true || value === false || value === "ask");
      if (!ok) {
        warnings.push({
          code: "RPK018",
          message: `Ignoring invalid permission declaration "${name}".`,
          path: MANIFEST_PATH,
        });
      }
      return ok;
    })
    .map(([name, value]) => ({
      name,
      decision: permissionDecision(value as RqtPackPermissionValue),
      sensitive: isSensitivePermission(name),
      group: name.split(".")[0] ?? name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function permissionDecision(value: RqtPackPermissionValue): RqtPackPermissionDecision {
  if (value === "ask") return "ask";
  return value ? "allow" : "deny";
}

function isSensitivePermission(name: string): boolean {
  return (
    name.startsWith("network.") ||
    name.startsWith("plugin.") ||
    name.startsWith("system.") ||
    name.startsWith("filesystem.write") ||
    name === "clipboard.write" ||
    name === "model.image"
  );
}

function normalizeRecommendations(manifest: RqtPackManifest): RqtPackRecommendationPreview[] {
  return [
    ...recommendationEntries("recommendations", manifest.recommendations),
    ...recommendationEntries("recommendedRuntime", manifest.recommendedRuntime),
    ...recommendationEntries("modelCompatibility", manifest.modelCompatibility),
  ].sort((a, b) => `${a.source}.${a.key}`.localeCompare(`${b.source}.${b.key}`));
}

function recommendationEntries(
  source: RqtPackRecommendationPreview["source"],
  raw: unknown,
): RqtPackRecommendationPreview[] {
  if (!isRecord(raw)) return [];
  return Object.entries(raw).map(([key, value]) => ({ key, value, source }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
